import {
  useStrings,
  useStringsWithPlaceholders,
} from "@1js/localization-components";
import type { InteractiveBadge, Prompt } from "@1js/pl-card-components";
import {
  BookmarkButtonTooltip,
  ConditionalWrap,
  InteractivePromptBadges,
  PromptRemove,
  PromptShare,
  UnBookmarkButtonTooltip,
} from "@1js/pl-card-components";
import type { Group } from "@1js/pl-types";
import {
  isExtensionPrompt,
  isMicrosoftPrompt,
  isNonSharedUserPrompt,
  isSharedPrompt,
  isUserCreatedExtensionPrompt,
} from "@1js/pl-utils";
import { useMsal } from "@azure/msal-react";
import type { ButtonProps, ToastIntent } from "@fluentui/react-components";
import {
  Button,
  Toaster,
  ToggleButton,
  Tooltip,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
  useId,
} from "@fluentui/react-components";
import type { FluentIconsProps } from "@fluentui/react-icons";
import {
  BookmarkFilled,
  BookmarkRegular,
  Copy20Regular,
  DeleteFilled,
  DeleteRegular,
  HeartFilled,
  HeartRegular,
  Share20Regular,
  bundleIcon,
} from "@fluentui/react-icons";
import parse from "html-react-parser";
import * as React from "react";
import { useShareToast } from "../../../hooks/toast/useShareToast";
import { useToast } from "../../../hooks/toast/useToast";
import { useFeatureEnablement } from "../../../hooks/useFeatureEnablement";
import { useFlightContext } from "../../../hooks/useFlightContext";
import { useHandoffService } from "../../../hooks/useHandoffService";
import { usePromptActions } from "../../../hooks/usePromptActions";
import { useSharePrompt } from "../../../hooks/useSharePrompt";
import { MetaOSAppContext } from "../../../pages/metaos/MetaOSAppContext";
import { LibraryContext } from "../../../pages/prompts/allPromptsLibrary/LibraryContext";
import { PropertyContext } from "../../../pages/PropertyContext.web";
import type { WebPrompt } from "../../data";
import { SignInToBookmark } from "../../SignInHeader/SignInHeader.web.string";
import {
  ActionsCopyPromptClipboard,
  ActionsCopyPromptTooltip,
  ActionsLikeButtonTooltip,
  ActionsPromptBookmarkSaved,
  ActionsPromptBookmarkSavedError,
  ActionsPromptBookmarkTooltip,
  ActionsPromptBookmarkUnSaved,
  ActionsPromptBookmarkUnSavedError,
  ActionsPromptDeleteTooltip,
  ActionsPromptLinkClipboard,
  ActionsPromptLinkClipboardBody,
  ActionsPromptLinkTooltip,
  ActionsTryInLabel,
  ActionsUnLikeButtonTooltip,
} from "./Actions.web.string";
type Props = {
  prompt: WebPrompt | Prompt;
  isBookmarkEnabled?: boolean;
  isBookmarked?: boolean;
  isAgentPromptSavingEnabled?: boolean;
  isPromptSavingEnabled?: boolean;
  isPromptSharingEnabled?: boolean;
  isPromptSharingViaEmailEnabled?: boolean;
  isPromptCopyLinkEnabled?: boolean;
  isPromptLikingEnabled?: boolean;
  onBookmarkToggled?: (
    prompt: Prompt,
    value?: boolean,
    toastNotify?: (
      intent: ToastIntent,
      title: string,
      body?: string,
      footer?: JSX.Element,
      headerButton?: JSX.Element
    ) => void
  ) => Promise<void>;
  onShareClicked?: (prompt: Prompt) => void;
  onCopyClicked?: (prompt: Prompt) => void;
  onDelete?: (prompt: Prompt) => void;
  onTryInAppBadgeAction?: (
    prompt: Prompt,
    product: string,
    tagRef: React.MutableRefObject<null>
  ) => void;
  isPromptSharingToWorkgroupEnabled?: boolean;
  onShareToWorkgroupClicked?: (prompts: Prompt[]) => void | Promise<void>;
  groups?: Group[];
  selectedGroupId?: string;
  onRemoveFromWorkgroupClicked?: (
    prompts: Prompt[],
    groupIds: string[]
  ) => void;
  isPromptRemovingFromWorkgroupEnabled?: boolean;
};

const useGoBoldStyles = makeStyles({
  mainContainer: {
    display: "flex",
    flexWrap: "wrap",
    ...shorthands.gap("8px"),
  },
  actionsIconContainer: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "flex-end",
    ...shorthands.gap("8px"),
  },
  shareButton: {
    ...shorthands.padding("5px"),
    maxWidth: "auto",
  },
  removeButton: {
    ...shorthands.padding("5px"),
    maxWidth: "auto",
  },
  actionsButtonContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "stretch",
    ...shorthands.gap("8px"),
  },
  buttonWithBorder: {
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    cursor: "pointer",
    "&:hover": {
      backgroundColor: tokens.colorSubtleBackgroundHover,
      border: `1px solid ${tokens.colorNeutralStroke1}`,
    },
    "&:active, &:hover:active": {
      backgroundColor: tokens.colorSubtleBackgroundPressed,
      border: `1px solid ${tokens.colorNeutralStroke1}`,
    },
  },
  bookmarkButton: {
    ...shorthands.padding("5px"),
    minWidth: "auto",
  },
  bookmarkIcon: {
    fontSize: "20px",
  },
});

const useStyles = makeStyles({
  mainContainer: {
    display: "flex",
    justifyContent: "space-between",
  },
  actionsIconContainer: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "flex-end",
    ...shorthands.gap("8px"),
  },
  shareButton: {
    ...shorthands.padding("5px"),
  },
  removeButton: {
    ...shorthands.padding("5px"),
    boxSizing: "content-box",
  },
  actionsButtonContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "stretch",
    ...shorthands.gap("8px"),
  },
  buttonWithBorder: {},
  bookmarkButton: {
    ...shorthands.padding("5px"),
    minWidth: "auto",
  },
  bookmarkIcon: {
    fontSize: "20px",
  },
});

const BookmarkIcon = bundleIcon(BookmarkFilled, BookmarkRegular);
const HeartIcon = bundleIcon(HeartFilled, HeartRegular);
const DeleteIcon = bundleIcon(DeleteFilled, DeleteRegular);

export const Actions: React.FC<Props> = ({
  prompt,
  isBookmarkEnabled,
  isBookmarked,
  isAgentPromptSavingEnabled,
  isPromptSavingEnabled,
  isPromptSharingEnabled,
  isPromptSharingViaEmailEnabled,
  isPromptCopyLinkEnabled = true,
  isPromptLikingEnabled,
  onBookmarkToggled,
  onShareClicked,
  onCopyClicked,
  onDelete,
  onTryInAppBadgeAction,
  isPromptSharingToWorkgroupEnabled,
  onShareToWorkgroupClicked,
  groups,
  onRemoveFromWorkgroupClicked,
  selectedGroupId,
  isPromptRemovingFromWorkgroupEnabled,
}) => {
  const strings = useStrings({
    ActionsCopyPromptClipboard,
    ActionsCopyPromptTooltip,
    ActionsPromptLinkClipboard,
    ActionsPromptLinkClipboardBody,
    ActionsPromptLinkTooltip,
    ActionsPromptBookmarkTooltip,
    UnBookmarkButtonTooltip,
    BookmarkButtonTooltip,
    ActionsPromptBookmarkSaved,
    ActionsPromptBookmarkUnSaved,
    ActionsPromptBookmarkSavedError,
    ActionsPromptBookmarkUnSavedError,
    ActionsPromptDeleteTooltip,
    ActionsLikeButtonTooltip,
    ActionsUnLikeButtonTooltip,
  });

  const stringsWithPlaceholders = useStringsWithPlaceholders({
    ActionsTryInLabel,
  });

  const { isTryInOnAuthed } = useFeatureEnablement();

  const { isHandoffSupported, isProductNeedsFile } = useHandoffService();

  const [siteSetting] = React.useContext(PropertyContext);
  const { state } = React.useContext(LibraryContext);
  const { includesAny } = useFeatureEnablement();
  const isCWCEligible = state.isUserEligibleForCompliantWebChat ?? false;
  const isBizChatCWCPrompt =
    prompt?.Products?.includes("BizChat") && prompt?.Products?.includes("CWC");
  const isCWCPrompt = prompt?.Products?.includes("CWC");

  const badges: InteractiveBadge[] = prompt.Products.map((product) => {
    return {
      id: product,
      needsFile: isProductNeedsFile(product, prompt),
      onAction:
        typeof onTryInAppBadgeAction === "function" &&
        isHandoffSupported(product, prompt)
          ? (tagRef: React.MutableRefObject<null>) =>
              onTryInAppBadgeAction(prompt, product, tagRef)
          : undefined,
    };
  });

  const bookmarkTooltip = isBookmarked
    ? strings.UnBookmarkButtonTooltip()
    : strings.BookmarkButtonTooltip();

  const likeTooltip = isBookmarked
    ? strings.ActionsUnLikeButtonTooltip()
    : strings.ActionsLikeButtonTooltip();

  const buttonStyleProps: ButtonProps = {
    appearance: "transparent",
  };

  const iconStyleProps: FluentIconsProps = {
    filled: isBookmarked,
  };

  const sharePrompt = useSharePrompt();

  const [vertical, setVertical] = React.useState(0);
  const toasterId = useId("toaster");
  const { notifyShareCopyLinkSuccess, notifyShareCopyLinkFail } =
    useShareToast(toasterId);
  const { notify: _notify } = useToast(toasterId, 5000, prompt.Title);
  const notify = (...props: Parameters<typeof _notify>) => {
    const dialog = document.getElementById("dialog-title-id");
    setVertical(dialog?.getBoundingClientRect().top || 108); // 108 is the default value for margin between the top of the page and the dialog.
    _notify(...props);
  };

  const { isFlightActive } = useFlightContext();
  const isGoBoldEnabled = isFlightActive("isGoBoldPromptCardEnabled");
  const goBoldStyles = useGoBoldStyles();
  const legacyStyles = useStyles();
  const styles = isGoBoldEnabled ? goBoldStyles : legacyStyles;

  const handleBookmarkClicked = () => {
    if (onBookmarkToggled) {
      onBookmarkToggled(prompt, !isBookmarked, notify);
    }
  };
  const { handleCopyClicked: handleCopyClickedHooked } = usePromptActions();

  const handleCopyClicked = () => {
    handleCopyClickedHooked(prompt, (prompt: Prompt) => {
      notify("success", strings.ActionsCopyPromptClipboard());
      onCopyClicked?.(prompt);
    });
  };

  const handleShareViaCopyLinkClicked = async (prompt: Prompt) => {
    try {
      await sharePrompt.shareUrlViaCopyLink(prompt);
      notifyShareCopyLinkSuccess(prompt);
      if (onShareClicked) {
        onShareClicked(prompt);
      }
    } catch (_) {
      notifyShareCopyLinkFail();
    }
  };

  const handleShareViaEmailClicked = async (prompt: Prompt) => {
    await sharePrompt.shareUrlViaEmail(prompt, instance);
    // TODO: we need a way to distinguish between the two types of sharing (copy link and email)
    if (onShareClicked) {
      onShareClicked(prompt);
    }
  };

  const isPromptRemovingFromWorkgroupEnabledInner =
    isPromptRemovingFromWorkgroupEnabled &&
    !!selectedGroupId &&
    prompt.Groups?.some((group) => group.Id === selectedGroupId);
  const isExtension = isExtensionPrompt(prompt);

  const metaOSAppContext = React.useContext(MetaOSAppContext);
  const msalInstance = useMsal();
  const instance =
    metaOSAppContext.isMosApp && metaOSAppContext.pca
      ? msalInstance.instance
      : undefined;

  const localizations = useStrings({
    SignInToBookmark,
  });

  let updatedBadges = badges;
  //For paid AI license users(M365 AI sku), we always prefer the BizChat badge over the CWC badge.
  // Handoff is also preferred to Work tab instead of Web tab. Handled by the Handoff service.
  if (
    isFlightActive("isTryInCWCForBusinessChatUsersEnabled") &&
    // Using specific license check here instead of using siteSetting.user.copilotLicensed property
    // because this is specific to Bizchat license check and the copilotLicensed property definition
    // from server could change in future to include other licenses.
    includesAny(siteSetting.licenses, ["CopilotBusinessChat", "Copilot"])
  ) {
    if (isBizChatCWCPrompt) {
      //Only show one badge for BizChat, remove the CWC badge.
      updatedBadges = updatedBadges.filter((badge) => badge.id !== "CWC");
    } else if (isCWCPrompt) {
      updatedBadges.forEach((badge) => {
        if (badge.id === "CWC") {
          badge.id = "CWCWithBizChatLabel";
        }
      });
    }
  }

  if (
    isFlightActive("isTryInCWCForNonAIUsersEnabled") &&
    isCWCEligible &&
    isBizChatCWCPrompt
  ) {
    //Only show one badge for CWC, remove the BizChat badge.
    updatedBadges = updatedBadges.filter((badge) => badge.id !== "BizChat");
  }

  return (
    <div className={styles.mainContainer}>
      <ConditionalWrap
        condition={!isGoBoldEnabled}
        wrapIfTrue={(children) => (
          <div className={styles.actionsButtonContainer}>{children}</div>
        )}
      >
        {(!isExtension ||
          (isExtension &&
            (isFlightActive("isTryInBizChatEnabledForAgents") ||
              isFlightActive("isTryInPowerPointEnabledForAgents") ||
              isFlightActive("isTryInWordEnabledForAgents")))) &&
          isTryInOnAuthed && (
            <InteractivePromptBadges
              data-testid="badge"
              badges={updatedBadges}
              maxNumber={20}
              displayMode="iconAndText"
              labelWrapper={stringsWithPlaceholders.ActionsTryInLabel}
              appearance="outline"
            />
          )}
      </ConditionalWrap>
      <div className={styles.actionsIconContainer}>
        <Toaster
          toasterId={toasterId}
          offset={{ vertical: vertical }}
          pauseOnHover
          pauseOnWindowBlur
          shortcuts={{
            focus: (event) => event.key === "Tab" && !event.shiftKey,
          }}
        />
        <Tooltip
          content={strings.ActionsCopyPromptTooltip()}
          relationship="label"
        >
          <Button
            data-testid="copy"
            icon={<Copy20Regular />}
            onClick={handleCopyClicked}
            className={isGoBoldEnabled ? styles.buttonWithBorder : undefined}
            {...buttonStyleProps}
          />
        </Tooltip>
        {!isExtension &&
          isBookmarkEnabled &&
          (isSharedPrompt(prompt) || isMicrosoftPrompt(prompt)) && (
            <Tooltip
              content={isPromptLikingEnabled ? likeTooltip : bookmarkTooltip}
              relationship="label"
            >
              <ToggleButton
                data-testid="bookmark"
                checked={isBookmarked}
                onClick={handleBookmarkClicked}
                className={mergeClasses(
                  styles.bookmarkButton,
                  isGoBoldEnabled && styles.buttonWithBorder
                )}
                {...buttonStyleProps}
              >
                {isPromptLikingEnabled ? (
                  <HeartIcon
                    {...iconStyleProps}
                    className={styles.bookmarkIcon}
                  />
                ) : (
                  <BookmarkIcon
                    {...iconStyleProps}
                    className={styles.bookmarkIcon}
                  />
                )}
              </ToggleButton>
            </Tooltip>
          )}
        {/* This is the code for putting in the fake book mark button that will encourage users to sign in. */}
        {isFlightActive("isSignInSuggestionEnabled") &&
          !metaOSAppContext.isMosApp &&
          !siteSetting.user && (
            <Tooltip
              content={parse(localizations.SignInToBookmark())}
              relationship="label"
            >
              <Button
                data-testid="unauthed-bookmark"
                icon={
                  isPromptLikingEnabled ? (
                    <HeartRegular {...iconStyleProps} />
                  ) : (
                    <BookmarkRegular {...iconStyleProps} />
                  )
                }
                className={
                  isGoBoldEnabled ? styles.buttonWithBorder : undefined
                }
                {...buttonStyleProps}
                disabled={true}
              />
            </Tooltip>
          )}
        {isPromptSavingEnabled &&
          isNonSharedUserPrompt(prompt) &&
          ((!isExtension && prompt.Origin === "User") ||
            (isAgentPromptSavingEnabled &&
              isUserCreatedExtensionPrompt(prompt))) && (
            <Tooltip
              content={strings.ActionsPromptDeleteTooltip()}
              relationship="label"
            >
              <ToggleButton
                data-testid="delete"
                checked={!isPromptLikingEnabled}
                onClick={() => onDelete && onDelete(prompt)}
                className={mergeClasses(
                  styles.bookmarkButton,
                  isGoBoldEnabled && styles.buttonWithBorder
                )}
                {...buttonStyleProps}
              >
                {isPromptLikingEnabled ? (
                  <DeleteIcon
                    {...iconStyleProps}
                    className={styles.bookmarkIcon}
                  />
                ) : (
                  <BookmarkIcon
                    {...iconStyleProps}
                    className={styles.bookmarkIcon}
                  />
                )}
              </ToggleButton>
            </Tooltip>
          )}
        {!isExtension && isPromptCopyLinkEnabled && isPromptSharingEnabled && (
          <PromptShare
            appearance="web"
            prompt={prompt}
            isPromptSharingViaEmailEnabled={isPromptSharingViaEmailEnabled}
            onShareViaCopyLinkClicked={handleShareViaCopyLinkClicked}
            onShareViaEmailClicked={handleShareViaEmailClicked}
            className={mergeClasses(
              styles.shareButton,
              isGoBoldEnabled && styles.buttonWithBorder
            )}
            isPromptSharingToWorkgroupEnabled={
              isPromptSharingToWorkgroupEnabled
            }
            onShareToWorkgroupClicked={onShareToWorkgroupClicked}
            groups={groups}
            displayMenuAboveOthers={isGoBoldEnabled}
          />
        )}
        {!isExtension &&
          isPromptCopyLinkEnabled &&
          !isPromptSharingEnabled &&
          prompt.Origin !== "User" && (
            <Tooltip
              content={strings.ActionsPromptLinkTooltip()}
              relationship="label"
            >
              <Button
                data-testid="share"
                icon={<Share20Regular />}
                onClick={() => {
                  handleShareViaCopyLinkClicked(prompt);
                }}
                className={
                  isGoBoldEnabled ? styles.buttonWithBorder : undefined
                }
                {...buttonStyleProps}
              />
            </Tooltip>
          )}
        {!isExtension && isPromptRemovingFromWorkgroupEnabledInner && (
          <PromptRemove
            onRemove={onRemoveFromWorkgroupClicked}
            prompt={prompt}
            groupId={selectedGroupId}
            isRemoving={prompt.RemovingState === "long"}
            className={mergeClasses(
              styles.removeButton,
              isGoBoldEnabled && styles.buttonWithBorder
            )}
          />
        )}
      </div>
    </div>
  );
};
